<template>
  <ElForm
    ref="campaignModel"
    class="add-search-campaign-general"
    :model="campaignModel"
    :rules="campaignRules"
    label-position="right"
    @submit.native.prevent="submitForm"
  >
    <ElFormItem prop="name" :label="$t('ADVERTISEMENT.CAMPAIGNS.NAME')">
      <ElInput v-model="campaignModel.name" name="name" required />
    </ElFormItem>

    <ElFormItem
      prop="packageName"
      :label="$t('ADVERTISEMENT.CAMPAIGNS.PACKAGE_NAME')"
    >
      <AlternativeSelect
        v-model="campaignModel.packageName"
        key-prop="packageName"
        :items="apps"
        label-prop="packageName"
        value-prop="packageName"
      />
    </ElFormItem>

    <ElFormItem
      prop="cloneFrom"
      :label="$t('ADVERTISEMENT.NEW_CAMPAIGN.CLONE_FROM')"
    >
      <AlternativeSelect
        v-model="campaignModel.cloneFrom"
        key-prop="id"
        :items="campaigns"
        label-prop="name"
        value-prop="id"
      />
      <p class="el-form-item__help">
        <i class="el-icon-warning" />
        {{ $t('ADVERTISEMENT.NEW_CAMPAIGN.CLONE_FROM_DESCRIPTION') }}
      </p>
    </ElFormItem>

    <ElFormItem prop="paused" :label="$t('ADVERTISEMENT.CAMPAIGNS.STATUS')">
      <AlternativeSelect
        v-model="campaignModel.paused"
        key-prop="value"
        :items="statusOptions"
      />
    </ElFormItem>

    <ElFormItem :label="$t('ADVERTISEMENT.CAMPAIGNS.START_DATE')">
      <UiDatePickerEndFuture
        :default-date="currentDate"
        @select-date="setStartDate"
      />
    </ElFormItem>

    <ElFormItem :label="$t('ADVERTISEMENT.CAMPAIGNS.END_DATE')">
      <UiDatePickerEndFuture
        :default-date="nextMonthDate"
        @select-date="setEndDate"
      />
    </ElFormItem>

    <ElFormItem prop="hasTracker">
      <ElCheckbox v-model="campaignModel.hasTracker">
        {{ $t('ADVERTISEMENT.CAMPAIGNS.CHOOSE_TO_HAVE_TRACKER') }}
      </ElCheckbox>
    </ElFormItem>

    <ElCollapseTransition>
      <div v-show="campaignModel.hasTracker">
        <FormItemWithHint
          prop="impressionTrackerLink"
          :label="$t('ADVERTISEMENT.CAMPAIGNS.IMPRESSION_TRACKER_LINK')"
          :hint="$t('HINTS.DISCOVERY.TRACKER_EXAMPLES')"
        >
          <ElInput
            v-model="campaignModel.impressionTrackerLink"
            type="url"
            dir="ltr"
          />
          <p class="el-form-item__help">
            <i class="el-icon-warning" />
            {{ $t('ADVERTISEMENT.CAMPAIGNS.AVAILABLE_TRACKERS') }}
          </p>
        </FormItemWithHint>
      </div>
    </ElCollapseTransition>
    <ElFormItem>
      <div class="add-search-campaign-general__actions">
        <ElButton
          type="primary"
          native-type="submit"
          size="large"
          :loading="isLoading"
          @click.native.prevent="submitForm"
        >
          {{ $t('ADVERTISEMENT.NEW_CAMPAIGN_BTN') }}
        </ElButton>
      </div>
    </ElFormItem>
  </ElForm>
</template>

<script>
import UiDatePickerEndFuture from '@@/common/UiDatePickerEndFuture';
import { dateFormat } from '@/utils/date';
import form from '@/mixins/form';
import AlternativeSelect from '@@/common/AlternativeSelect';
import requiredWithMessage from '@/utils/validation-rules/required';
import FormItemWithHint from '@/components/common/FormItemWithHint.vue';

export default {
  name: 'AddSearchCampaignGeneral',

  components: {
    UiDatePickerEndFuture,
    AlternativeSelect,
    FormItemWithHint,
  },

  mixins: [form],

  props: {
    resetController: {
      type: Object,
      required: true,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    apps: {
      type: Array,
      required: true,
    },
    campaigns: {
      type: Array,
      required: true,
    },
  },

  data() {
    return {
      campaignModel: {
        name: '',
        packageName: '',
        budget: 0,
        startDate: '',
        endDate: '',
        paused: false,
        hasTracker: false,
        impressionTrackerLink: null,
        cloneFrom: null,
      },
    };
  },

  computed: {
    statusOptions() {
      return [
        {
          value: false,
          label: this.$t('ADVERTISEMENT.CAMPAIGNS.ACTIVE'),
        },
        {
          value: true,
          label: this.$t('ADVERTISEMENT.CAMPAIGNS.PAUSED'),
        },
      ];
    },
    currentDate() {
      return Date.now();
    },

    nextMonthDate() {
      const date = new Date();
      return date.setMonth(date.getMonth() + 1);
    },

    campaignRules() {
      return {
        name: {
          ...requiredWithMessage(this.$t('VALIDATION_REQUIRED_ERR')),
          type: 'string',
        },
        packageName: {
          ...requiredWithMessage(this.$t('VALIDATION_REQUIRED_ERR')),
          type: 'string',
        },
        startDate: {
          ...requiredWithMessage(this.$t('VALIDATION_REQUIRED_ERR')),
          type: 'date',
        },
        endDate: {
          ...requiredWithMessage(this.$t('VALIDATION_REQUIRED_ERR')),
          type: 'date',
        },
        paused: {
          ...requiredWithMessage(this.$t('VALIDATION_REQUIRED_ERR')),
          type: 'boolean',
        },
        impressionTrackerLink: {
          type: 'url',
          message: this.$t('VALIDATION.URL_IS_INVALID'),
          trigger: 'blur',
        },
      };
    },
  },

  methods: {
    created() {
      this.resetController.onReset(this.reset);
    },
    beforeDestroy() {
      this.resetController.removeOnReset(this.reset);
    },
    reset() {
      this.resetForm('campaignModel');
    },
    submitForm() {
      if (!this.isValid('campaignModel')) {
        return false;
      }

      this.$emit('submit', this.campaignModel);
    },

    setStartDate(selectedDate) {
      this.$set(
        this.campaignModel,
        'startDate',
        dateFormat()('YYYY-MM-DDT00:00:00')(selectedDate),
      );
    },

    setEndDate(selectedDate) {
      this.$set(
        this.campaignModel,
        'endDate',
        dateFormat()('YYYY-MM-DDT23:59:59')(selectedDate),
      );
    },
  },
};
</script>

<style lang="scss">
.add-search-campaign-general {
  &__actions {
    display: flex;
    justify-content: flex-end;
  }
}
</style>
